import type { ParentType as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { useGetInternalAuditResultsByParentId } from 'src/hooks/queries/internal-audit/useGetInternalAuditResultsByParentId';

import { getFriendlyId } from '@/utils/friendlyId';

import type {
  InternalAuditResultFields,
  InternalAuditResultTypeValue,
} from './types';

type GetInternalAuditResultsByParentIdData = NonNullable<
  ReturnType<typeof useGetInternalAuditResultsByParentId>['data']
>;

export const getParentTitle = (record: InternalAuditResultFields) => {
  switch (record.ResultType) {
    case 'document_internal_audit_result':
      return (
        (
          record as Partial<
            GetInternalAuditResultsByParentIdData['document_internal_audit_result'][0]
          >
        ).parents?.[0]?.document?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'obligation_internal_audit_result':
      return (
        (
          record as Partial<
            GetInternalAuditResultsByParentIdData['obligation_internal_audit_result'][0]
          >
        ).parents?.[0]?.obligation?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'risk_controlled_internal_audit_result':
      return (
        (
          record as Partial<
            GetInternalAuditResultsByParentIdData['risk_controlled_internal_audit_result'][0]
          >
        ).parents?.[0]?.risk?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'risk_uncontrolled_internal_audit_result':
      return (
        (
          record as Partial<
            GetInternalAuditResultsByParentIdData['risk_uncontrolled_internal_audit_result'][0]
          >
        ).parents?.[0]?.risk?.Title ??
        getFriendlyId(
          record.parents?.[0]?.node?.ObjectType as Parent_Type_Enum,
          record.parents?.[0]?.node?.SequentialId
        )
      );
    case 'control_test_internal_audit_result':
      return (
        (
          record as Partial<
            GetInternalAuditResultsByParentIdData['control_test_internal_audit_result'][0]
          >
        )?.parent?.Title ?? '-'
      );
  }
};

export const decorateWithControlType = (
  value: string,
  getControlTypeLabel: (
    controlType: Risk_Assessment_Result_Control_Type_Enum
  ) => string,
  // only the discriminator is read here, so don't demand the whole row
  record: { ResultType: InternalAuditResultTypeValue }
) => {
  if (record.ResultType === 'risk_controlled_internal_audit_result') {
    return `${value} - ${getControlTypeLabel(Risk_Assessment_Result_Control_Type_Enum.Controlled)}`;
  }

  if (record.ResultType === 'risk_uncontrolled_internal_audit_result') {
    return `${value} - ${getControlTypeLabel(Risk_Assessment_Result_Control_Type_Enum.Uncontrolled)}`;
  }

  return value;
};
